import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { initAuth, resetAuthInit } from './auth';
import './LoginPage.css';

const LoginPage = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [searchParams] = useSearchParams();
  const [appleEnabled, setAppleEnabled] = useState(false);
  // 'choose' = the 3-option screen (no scroll); 'email' = the unfolded email form.
  const [mode, setMode] = useState('choose');

  // OAuth callback path: server already set the httpOnly cookie before
  // redirecting back; just bootstrap the user cache and continue.
  React.useEffect(() => {
    if (searchParams.get('logged_in') === '1') {
      resetAuthInit();
      initAuth().then((u) => { if (u && onLoginSuccess) onLoginSuccess(); });
    }
  }, [searchParams, onLoginSuccess]);

  // Show "Sign in with Apple" only once the server has Apple OAuth configured.
  React.useEffect(() => {
    fetch('/api/apple/enabled')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.enabled) setAppleEnabled(true); })
      .catch(() => {});
  }, []);

  // Surface OAuth provider errors (e.g. ?error=apple_failed) on the login page.
  React.useEffect(() => {
    const e = searchParams.get('error');
    if (e === 'apple_failed') setError('Apple sign-in failed — please try again.');
    else if (e === 'apple_unavailable') setError('Apple sign-in is not available yet.');
    else if (e === 'google_failed') setError('Google sign-in failed — please try again.');
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Normalize email to match how signup stores it (lowercased). Prevents the
      // "signed up but can't log in" trap when a mobile keyboard or saved credential
      // capitalizes the address.
      const normEmail = email.trim().toLowerCase();
      // credentials: 'include' so the Set-Cookie from the server lands in the
      // browser cookie jar. No JWT is read from the JSON body anymore.
      const response = await fetch('/auth0/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `email=${encodeURIComponent(normEmail)}&password=${encodeURIComponent(password)}&remember=${remember ? 'true' : 'false'}`
      });
      if (!response.ok) { setError('Invalid email or password'); setLoading(false); return; }
      // Drain the body to free the connection; do NOT read tokens from it.
      try { await response.text(); } catch (_) {}
      resetAuthInit();
      const u = await initAuth();
      if (u) {
        if (onLoginSuccess) onLoginSuccess();
      } else {
        setError('Login failed — session not established');
      }
    } catch (err) { setError('Connection error — please try again'); }
    setLoading(false);
  };

  const handleGoogleLogin = () => { window.location.href = '/auth/google'; };
  const handleAppleLogin = () => { window.location.href = '/auth/apple'; };

  return (
    <div className="login-page">
      <div className="login-card">
        {mode === 'email' && (
          <button type="button" className="login-back-btn" onClick={() => { setError(''); setMode('choose'); }} data-testid="login-back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
            All sign-in options
          </button>
        )}

        <div className="login-header">
          <img src="/assets/dynamic/NumFreeSymbol.png" className="login-logo" alt="" />
          <h2>Welcome back</h2>
          <p className="login-tagline">Sign in to your numbers, calls, and assistant.</p>
        </div>

        {error && <div className="login-error">{error}</div>}

        {mode === 'choose' ? (
          <div className="login-choices">
            <button type="button" className="login-email-btn" onClick={() => { setError(''); setMode('email'); }} data-testid="login-choose-email">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 6L2 7"/></svg>
              Sign in with email
            </button>

            <button type="button" className="login-google-btn" onClick={handleGoogleLogin} data-testid="login-google">
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>

            {appleEnabled && (
              <button type="button" className="login-apple-btn" onClick={handleAppleLogin} data-testid="login-apple">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.4-.9-1.75.03-3.36 1.02-4.26 2.58-1.82 3.16-.47 7.83 1.3 10.39.86 1.25 1.89 2.66 3.24 2.61 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.39.81 1.4-.02 2.29-1.28 3.15-2.54.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.73-1.05-2.75-4.17zM14.6 4.59c.72-.87 1.2-2.08 1.07-3.29-1.03.04-2.28.69-3.02 1.56-.67.77-1.25 2-.1 3.17 1.13.09 2.33-.57 3.05-1.44z"/></svg>
                Sign in with Apple
              </button>
            )}
          </div>
        ) : (
          <form className="login-form" onSubmit={handleSubmit} data-testid="login-form">
            <div className="login-field">
              <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required autoFocus autoCapitalize="none" autoCorrect="off" spellCheck="false" />
            </div>
            <div className="login-field">
              <input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required />
            </div>
            <label className="login-remember">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} data-testid="login-remember" />
              <span>Keep me signed in for 30 days</span>
            </label>
            <button className="login-btn" data-testid="login-submit" type="submit" disabled={loading}>
              {loading && <span className="login-spinner" />}
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        <div className="login-alt-hint">New to NumFree?</div>
        <Link className="login-alt-btn" to="/signup" data-testid="login-go-signup">
          Create an account
        </Link>
      </div>
    </div>
  );
};

export default LoginPage;
